import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ChevronDown as ChevronDownIcon,
    Edit3 as Edit3Icon,
    LogOut as LogOutIcon,
    Mail as MailIcon,
    Settings2 as Settings2Icon,
    X as XIcon,
} from "lucide-react";
import { Group, Panel, Separator, useDefaultLayout } from "react-resizable-panels";
import { useNavigate, useParams } from "react-router-dom";
import { message, messageDelete, messageList, messageMove, normalizeMessage, setFlagged, setSeen, threadList } from "./api";
import type { Folder, Message, MessageListItem } from "./types";
import { FolderSidebar, ROLE_ICON, roleFor } from "./components/FolderSidebar";
import { MessageListPane } from "./components/MessageListPane";
import { MessageViewer } from "./components/MessageViewer";
import { rememberFolder, useMail } from "./MailApp";
import { useIsMobile } from "./useIsMobile";
import { senderName } from "./utils";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function MailShell() {
    const { loginData, folderList, setFolderList, refreshFolders, onLogout } = useMail();
    const navigate = useNavigate();
    const { folder: folderParam, uid: uidParam } = useParams();

    /* Per-user page size (defaults to 50 when unset). */
    const pageSize = Math.min(100, Math.max(10, loginData.MessagesPerPage ?? 50));

    const folder = folderParam ? decodeURIComponent(folderParam) : null;
    const uid = uidParam ? Number(uidParam) : null;

    const [messages, setMessages] = useState<MessageListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState("");
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState("");
    const [folderMenuOpen, setFolderMenuOpen] = useState(false);

    const [viewer, setViewer] = useState<Message | null>(null);
    const [viewerLoading, setViewerLoading] = useState(false);
    const [viewerError, setViewerError] = useState("");
    const [conversation, setConversation] = useState<Message[]>([]);
    const [conversationLoading, setConversationLoading] = useState(false);

    const folderObj: Folder | null = folderList.find((f) => f.fullName === folder) ?? null;

    /* ---- message list ---- */
    useEffect(() => {
        if (!folder) return;
        let cancelled = false;
        setListLoading(true);
        setListError("");
        setViewer(null);
        setViewerLoading(false);
        messageList({ folder, limit: pageSize, search })
            .then((result) => {
                if (cancelled) return;
                setMessages(result["@Collection"]);
                setTotal(result.totalEmails);
            })
            .catch((err) => {
                if (!cancelled) setListError(err instanceof Error ? err.message : "Failed to load messages");
            })
            .finally(() => {
                if (!cancelled) setListLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [folder, search, pageSize]);

    /* ---- message body ---- */
    useEffect(() => {
        if (!folder || !uid) {
            setViewer(null);
            setConversation([]);
            return;
        }
        let cancelled = false;
        setViewerLoading(true);
        setViewerError("");
        setViewer(null);
        setConversation([]);
        message(folder, uid, loginData.accountHash)
            .then((full) => {
                if (cancelled) return;
                setViewer(full);
                markSeen(full);
                // Try to expand the thread; if the server has no THREAD
                // capability it returns just this message — fine, single view.
                setConversationLoading(true);
                threadList(folder, uid)
                    .then((result) => {
                        if (!cancelled) {
                            // Thread items are list-level (no bodies); the viewer
                            // renders headers + "Open message" for those.
                            const coll = result["@Collection"].map((item) => normalizeMessage(item) as Message);
                            setConversation(coll.length > 1 ? coll : []);
                        }
                    })
                    .catch(() => {
                        if (!cancelled) setConversation([]);
                    })
                    .finally(() => {
                        if (!cancelled) setConversationLoading(false);
                    });
            })
            .catch((err) => {
                if (!cancelled) setViewerError(err instanceof Error ? err.message : "Failed to load message");
            })
            .finally(() => {
                if (!cancelled) setViewerLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folder, uid, loginData.accountHash]);

    /* Mark as read (optimistic) when a message is opened. */
    const markSeen = useCallback(
        (full: Message) => {
            if (full.flags.includes("\\seen")) return;
            setSeen(full.folder, [full.uid], true).catch(() => {});
            setMessages((prev) =>
                prev.map((m) =>
                    m.uid === full.uid && !m.flags.includes("\\seen")
                        ? { ...m, flags: [...m.flags, "\\seen"] }
                        : m
                )
            );
            setFolderList((prev) =>
                prev.map((f) =>
                    f.fullName === full.folder && f.unreadEmails > 0
                        ? { ...f, unreadEmails: f.unreadEmails - 1 }
                        : f
                )
            );
        },
        [setFolderList]
    );

    /* ---- pagination ---- */
    const loadMore = useCallback(async () => {
        if (!folder || loadingMore) return;
        setLoadingMore(true);
        try {
            const result = await messageList({ folder, offset: messages.length, limit: pageSize, search });
            setMessages((prev) => [...prev, ...result["@Collection"]]);
            setTotal(result.totalEmails);
        } catch {
            /* keep current list */
        } finally {
            setLoadingMore(false);
        }
    }, [folder, loadingMore, messages.length, search, pageSize]);

    /* ---- silent polling: folders + current list every 5 min ---- */
    const desktopNotifEnabled = !!loginData.DesktopNotifications;
    useEffect(() => {
        if (search) return; // don't poll while a search is active
        const inboxName =
            folderList.find((f) => (f.role ?? "").toLowerCase() === "inbox")?.fullName ?? "INBOX";
        const id = setInterval(() => {
            refreshFolders();
            if (folder) {
                messageList({ folder, limit: pageSize })
                    .then((result) => {
                        // Desktop notification for new INBOX mail (like upstream
                        // Messagelist#notifyNewMessages). Only when the tab is
                        // hidden — no point interrupting active reading.
                        const fresh = result.newMessages ?? [];
                        if (
                            desktopNotifEnabled &&
                            folder === inboxName &&
                            fresh.length &&
                            typeof Notification !== "undefined" &&
                            Notification.permission === "granted" &&
                            document.hidden
                        ) {
                            const first = result["@Collection"].find((m) => fresh.includes(String(m.uid)));
                            new Notification(loginData.Email || loginData.mainEmail, {
                                body:
                                    fresh.length === 1 && first
                                        ? `${senderName(first.from)} — ${first.subject || "(no subject)"}`
                                        : `${fresh.length} new messages`,
                                tag: "df-new-mail",
                            });
                        }
                        setMessages((prev) => (prev.length ? prev : result["@Collection"]));
                        setTotal(result.totalEmails);
                    })
                    .catch(() => {});
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [folder, refreshFolders, search, pageSize, folderList, desktopNotifEnabled, loginData.Email, loginData.mainEmail]);

    /* ---- folder roles (Trash / Archive / …) ---- */
    const trashFolder = useMemo(
        () => folderList.find((f) => (f.role ?? "").toLowerCase() === "trash")?.fullName ?? "Trash",
        [folderList]
    );
    const archiveFolderObj = useMemo(
        () => folderList.find((f) => (f.role ?? "").toLowerCase() === "archive"),
        [folderList]
    );
    const archiveFolder = archiveFolderObj?.fullName ?? "";
    const archiveAllowed = !!archiveFolderObj && folder !== archiveFolder;
    const isTrash = folder === trashFolder;

    /* ---- message actions (optimistic removal, refresh on success) ---- */
    const afterMutate = useCallback(() => {
        // Reload both the list and the folder sidebar counters.
        if (folder) {
            messageList({ folder, limit: pageSize, search })
                .then((result) => {
                    setMessages(result["@Collection"]);
                    setTotal(result.totalEmails);
                })
                .catch(() => {});
        }
        refreshFolders();
    }, [folder, pageSize, search, refreshFolders]);

    const deleteMessages = useCallback(
        (uids: number[]) => {
            if (!folder || !uids.length) return;
            if (isTrash) {
                messageDelete(folder, uids)
                    .then(afterMutate)
                    .catch(() => window.alert("Failed to delete message"));
            } else {
                messageMove(folder, trashFolder, uids, true)
                    .then(afterMutate)
                    .catch(() => window.alert("Failed to delete message"));
            }
        },
        [folder, isTrash, trashFolder, afterMutate]
    );

    const archiveMessages = useCallback(
        (uids: number[]) => {
            if (!folder || !uids.length || !archiveFolder) return;
            messageMove(folder, archiveFolder, uids)
                .then(afterMutate)
                .catch(() => window.alert("Failed to archive message"));
        },
        [folder, archiveFolder, afterMutate]
    );

    const moveMessages = useCallback(
        (uids: number[], toFolder: string) => {
            if (!folder || !uids.length || toFolder === folder) return;
            messageMove(folder, toFolder, uids)
                .then(afterMutate)
                .catch(() => window.alert("Failed to move message"));
        },
        [folder, afterMutate]
    );

    const toggleFlag = useCallback(
        (uids: number[], flagged: boolean) => {
            if (!folder || !uids.length) return;
            setFlagged(folder, uids, flagged)
                .then(() => {
                    // Optimistically update local flags so the ★ reflects at once.
                    setMessages((prev) =>
                        prev.map((m) =>
                            uids.includes(m.uid)
                                ? {
                                      ...m,
                                      flags: flagged
                                          ? [...new Set([...m.flags, "\\flagged"])]
                                          : m.flags.filter((f) => f !== "\\flagged"),
                                  }
                                : m
                        )
                    );
                })
                .catch(() => window.alert("Failed to update flag"));
        },
        [folder]
    );

    const markRead = useCallback(
        (uids: number[], read: boolean) => {
            if (!folder || !uids.length) return;
            setSeen(folder, uids, read)
                .then(() => {
                    setMessages((prev) =>
                        prev.map((m) =>
                            uids.includes(m.uid)
                                ? {
                                      ...m,
                                      flags: read
                                          ? [...new Set([...m.flags, "\\seen"])]
                                          : m.flags.filter((f) => f !== "\\seen"),
                                  }
                                : m
                        )
                    );
                    refreshFolders();
                })
                .catch(() => window.alert("Failed to update read state"));
        },
        [folder, refreshFolders]
    );

    const moveTargets = useMemo(
        () =>
            folderList
                .filter((f) => f.fullName !== folder && (f.role ?? "").toLowerCase() !== "trash")
                .sort((a, b) => a.name.localeCompare(b.name)),
        [folderList, folder]
    );

    /* ---- navigation helpers ---- */
    const openFolder = useCallback(
        (name: string) => {
            setSearch(""); // fresh search per folder
            rememberFolder(name);
            navigate(`/${encodeURIComponent(name)}`);
        },
        [navigate]
    );

    const openMessage = useCallback(
        (item: MessageListItem) => {
            rememberFolder(item.folder);
            navigate(`/${encodeURIComponent(item.folder)}/${item.uid}`);
        },
        [navigate]
    );

    const closeViewer = useCallback(() => {
        if (folder) navigate(`/${encodeURIComponent(folder)}`);
    }, [folder, navigate]);

    const hasMore = messages.length < total;

    // v4 sizes: numbers are px, strings without units are percentages.
    const layout = useDefaultLayout({ id: "df-mail-layout-v2", storage: localStorage });

    /* Mobile (<768px): stack the panes vertically — folders on top (compact),
     * then the message list, then the viewer. The viewer hides unless a
     * message is open, to keep the list usable on small screens. */
    const isMobile = useIsMobile();
    if (isMobile) {
        return (
            <div className="flex h-screen flex-col overflow-hidden bg-[#0b0b0f] text-white">
                <div className="flex h-12 shrink-0 items-center gap-2 border-b border-white/10 bg-[#0a0a0b] px-3">
                    <button
                        type="button"
                        onClick={() => setFolderMenuOpen(true)}
                        aria-label="Folders"
                        className="flex min-w-0 cursor-pointer items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-bold text-zinc-100 transition-colors hover:bg-white/10"
                    >
                        <span className="truncate">{folder ? folderObj?.name ?? folder : "Folders"}</span>
                        <ChevronDownIcon size={13} className="shrink-0 text-zinc-500" />
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/compose")}
                        className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 px-3 py-1.5 text-xs font-bold text-white"
                    >
                        <Edit3Icon size={13} /> Compose
                    </button>
                    <button
                        type="button"
                        onClick={onLogout}
                        className="ml-auto cursor-pointer rounded-lg p-1.5 text-zinc-500 hover:text-red-400"
                        title="Sign out"
                    >
                        <LogOutIcon size={15} />
                    </button>
                </div>

                {uid || viewerLoading ? (
                    /* Reading a message: viewer fills the screen, back goes to list. */
                    <div className="min-h-0 flex-1">
                        <MessageViewer
                            message={viewer ?? ({ uid: uid ?? 0, folder: folder ?? "" } as Message)}
                            conversation={conversation}
                            conversationLoading={conversationLoading}
                            accountHash={loginData.accountHash}
                            loading={viewerLoading || !viewer}
                            error={viewerError}
                            onClose={closeViewer}
                            onOpenMessage={(f, u) => {
                                rememberFolder(f);
                                navigate(`/${encodeURIComponent(f)}/${u}`);
                            }}
                            onCompose={(mode, msg) =>
                                navigate(`/compose?mode=${mode}&folder=${encodeURIComponent(msg.folder)}&uid=${msg.uid}`)
                            }
                            onDelete={(msg) => {
                                deleteMessages([msg.uid]);
                                closeViewer();
                            }}
                            onArchive={(msg) => {
                                archiveMessages([msg.uid]);
                                closeViewer();
                            }}
                            onToggleFlag={(msg) =>
                                toggleFlag([msg.uid], !(msg.flags ?? []).includes("\\flagged"))
                            }
                        />
                    </div>
                ) : folderObj ? (
                    <div className="min-h-0 flex-1">
                        <MessageListPane
                            folder={folderObj}
                            messages={messages}
                            total={total}
                            loading={listLoading}
                            error={listError}
                            selectedUid={uid}
                            onSelect={openMessage}
                            onRefresh={() => {
                                if (folder) {
                                    messageList({ folder, limit: pageSize, search })
                                        .then((result) => {
                                            setMessages(result["@Collection"]);
                                            setTotal(result.totalEmails);
                                            setListError("");
                                        })
                                        .catch((err) =>
                                            setListError(err instanceof Error ? err.message : "Failed to refresh")
                                        );
                                }
                            }}
                            hasMore={hasMore}
                            loadingMore={loadingMore}
                            onLoadMore={loadMore}
                            search={search}
                            onSearchChange={setSearch}
                            onDelete={deleteMessages}
                            onArchive={archiveMessages}
                            onMove={moveMessages}
                            onToggleFlag={toggleFlag}
                            onMarkRead={markRead}
                            moveTargets={moveTargets}
                            inTrash={isTrash}
                            archiveAllowed={archiveAllowed}
                        />
                    </div>
                ) : null}

                <MobileFolderDrawer
                    open={folderMenuOpen}
                    onClose={() => setFolderMenuOpen(false)}
                    folders={folderList}
                    current={folder}
                    onSelect={(name) => {
                        setFolderMenuOpen(false);
                        openFolder(name);
                    }}
                    accountEmail={loginData.Email || loginData.mainEmail}
                    onSettings={() => navigate("/settings")}
                    onLogout={onLogout}
                />
            </div>
        );
    }

    return (
        <div className="flex h-screen overflow-hidden bg-[#0b0b0f] text-white">
            <Group
                orientation="horizontal"
                defaultLayout={layout.defaultLayout}
                onLayoutChanged={layout.onLayoutChanged}
                resizeTargetMinimumSize={{ coarse: 16, fine: 4 }}
                className="h-full"
            >
                {/* Folders */}
                <Panel defaultSize="17" minSize="12" maxSize="28" className="min-w-0">
                    <FolderSidebar
                        folders={folderList}
                        current={folder}
                        onSelect={openFolder}
                        accountEmail={loginData.Email || loginData.mainEmail}
                        onLogout={onLogout}
                        onCompose={() => navigate("/compose")}
                        onSettings={() => navigate("/settings")}
                        onEnableNotifications={
                            typeof Notification !== "undefined" &&
                            Notification.permission === "default" &&
                            loginData.DesktopNotifications
                                ? () => {
                                      Notification.requestPermission().catch(() => {});
                                  }
                                : undefined
                        }
                    />
                </Panel>

                <ResizeHandle />

                {/* Message list */}
                <Panel defaultSize="34" minSize="22" className="min-w-0">
                    {folderObj ? (
                        <MessageListPane
                            folder={folderObj}
                            messages={messages}
                            total={total}
                            loading={listLoading}
                            error={listError}
                            selectedUid={uid}
                            onSelect={openMessage}
                            onRefresh={() => {
                                if (folder) {
                                    messageList({ folder, limit: pageSize, search })
                                        .then((result) => {
                                            setMessages(result["@Collection"]);
                                            setTotal(result.totalEmails);
                                            setListError("");
                                        })
                                        .catch((err) =>
                                            setListError(err instanceof Error ? err.message : "Failed to refresh")
                                        );
                                }
                            }}
                            hasMore={hasMore}
                            loadingMore={loadingMore}
                            onLoadMore={loadMore}
                            search={search}
                            onSearchChange={setSearch}
                            onDelete={deleteMessages}
                            onArchive={archiveMessages}
                            onMove={moveMessages}
                            onToggleFlag={toggleFlag}
                            onMarkRead={markRead}
                            moveTargets={moveTargets}
                            inTrash={isTrash}
                            archiveAllowed={archiveAllowed}
                        />
                    ) : null}
                </Panel>

                <ResizeHandle />

                {/* Viewer */}
                <Panel minSize="25" className="min-w-0">
                    {viewer || viewerLoading || uid ? (
                        <MessageViewer
                            message={viewer ?? ({ uid: uid ?? 0, folder: folder ?? "" } as Message)}
                            conversation={conversation}
                            conversationLoading={conversationLoading}
                            accountHash={loginData.accountHash}
                            loading={viewerLoading || !viewer}
                            error={viewerError}
                            onClose={closeViewer}
                            onOpenMessage={(f, u) => {
                                rememberFolder(f);
                                navigate(`/${encodeURIComponent(f)}/${u}`);
                            }}
                            onCompose={(mode, msg) =>
                                navigate(`/compose?mode=${mode}&folder=${encodeURIComponent(msg.folder)}&uid=${msg.uid}`)
                            }
                            onDelete={(msg) => {
                                deleteMessages([msg.uid]);
                                closeViewer();
                            }}
                            onArchive={(msg) => {
                                archiveMessages([msg.uid]);
                                closeViewer();
                            }}
                            onToggleFlag={(msg) =>
                                toggleFlag([msg.uid], !(msg.flags ?? []).includes("\\flagged"))
                            }
                        />
                    ) : (
                        <section className="flex h-full items-center justify-center bg-[#0b0b0f]">
                            <div className="flex flex-col items-center gap-3 text-center">
                                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-2xl opacity-50">
                                    ✉
                                </div>
                                <p className="text-sm font-medium text-zinc-500">Select a message to read it</p>
                            </div>
                        </section>
                    )}
                </Panel>
            </Group>
        </div>
    );
}

function ResizeHandle() {
    return (
        <Separator className="w-px bg-white/10 outline-none transition-colors hover:bg-orange-400/50 active:bg-orange-400/70 focus-visible:bg-orange-400/70" />
    );
}

/* Mobile folder navigation — a slide-in drawer mirroring the desktop
 * FolderSidebar (icons, unread badges, account footer). */
function MobileFolderDrawer({
    open,
    onClose,
    folders,
    current,
    onSelect,
    accountEmail,
    onSettings,
    onLogout,
}: {
    open: boolean;
    onClose: () => void;
    folders: Folder[];
    current: string | null;
    onSelect: (folder: string) => void;
    accountEmail: string;
    onSettings: () => void;
    onLogout: () => void;
}) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[110]">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden />
            <div className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-white/10 bg-[#0a0a0b] shadow-2xl">
                <div className="flex items-center justify-between px-4 pb-3 pt-5">
                    <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-orange-500/20 bg-orange-500/10 text-sm text-orange-400">
                            ✦
                        </div>
                        <div className="text-sm font-black tracking-tight text-white">DiscordForge Mail</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close folders"
                        className="cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
                    >
                        <XIcon size={16} />
                    </button>
                </div>

                <nav className="custom-scrollbar flex-1 space-y-0.5 overflow-y-auto px-2.5 pb-3">
                    {folders.map((folder) => {
                        const Icon = ROLE_ICON[roleFor(folder)] ?? MailIcon;
                        const selected = folder.fullName === current;
                        return (
                            <button
                                key={folder.fullName}
                                type="button"
                                onClick={() => onSelect(folder.fullName)}
                                className={`flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-[13px] font-medium transition-all ${
                                    selected
                                        ? "border border-orange-500/25 bg-orange-500/10 text-orange-400"
                                        : "border border-transparent text-zinc-400 hover:bg-white/5 hover:text-zinc-100"
                                }`}
                            >
                                <span className="flex min-w-0 items-center gap-2.5">
                                    <Icon size={15} className="shrink-0 opacity-70" />
                                    <span className="truncate">{folder.name}</span>
                                </span>
                                <span className="flex shrink-0 items-center gap-1.5">
                                    {folder.unreadEmails > 0 && (
                                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-black text-white shadow-[0_0_12px_rgba(249,115,22,0.4)]">
                                            {folder.unreadEmails}
                                        </span>
                                    )}
                                    {folder.totalEmails > 0 && folder.unreadEmails === 0 && (
                                        <span className="text-[10px] font-semibold tabular-nums text-zinc-600">
                                            {folder.totalEmails}
                                        </span>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </nav>

                <div className="border-t border-white/10 p-3">
                    <div className="flex items-center justify-between gap-2 rounded-xl px-2 py-1.5">
                        <div className="flex min-w-0 items-center gap-2.5">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0e0e11] text-[11px] font-black text-orange-400 ring-1 ring-white/10">
                                {((accountEmail || "?")[0] ?? "?").toUpperCase()}
                            </div>
                            <div className="min-w-0 leading-tight">
                                <div className="truncate text-xs font-semibold text-zinc-200">{accountEmail}</div>
                                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Connected</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onLogout}
                            title="Sign out"
                            className="cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-red-400"
                        >
                            <LogOutIcon size={14} />
                        </button>
                    </div>
                    <div className="mt-2 flex items-center gap-1 px-2">
                        <button
                            type="button"
                            onClick={onSettings}
                            title="Settings"
                            className="cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
                        >
                            <Settings2Icon size={14} />
                        </button>
                        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
                            Settings
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
